import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { ListView } from '@/shared/components/custom/list-view/components/ListView';
import { getPerformanceTableColumns } from '../adapters/performanceTableColumns';
import type { MemberTableEntry } from '../types';

interface PerformanceTableViewProps {
  entries: MemberTableEntry[];
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
}

export function PerformanceTableView({
  entries,
  isLoading = false,
  isError = false,
  onRetry,
}: PerformanceTableViewProps) {
  const { t } = useTranslation('laborPerformance');
  const columns = getPerformanceTableColumns(t);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-14 w-full rounded" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div
        data-testid="labor-performance-table-error"
        className="rounded-lg border border-red-200 bg-red-50 p-4"
      >
        <p className="text-sm font-medium text-red-800">{t('table.error')}</p>
        {onRetry && (
          <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
            {t('states.retry')}
          </Button>
        )}
      </div>
    );
  }

  return (
    <ListView
      data={entries}
      columns={columns}
      getRowId={(row) => row.memberId}
      data-testid="labor-performance-table"
    />
  );
}
